import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import {
  AlertTriangle, CheckCircle2, Download, FileDown, FileImage, LayoutGrid, Loader2,
  Maximize2, Plus, Printer, Replace, Sticker as StickerIcon, Trash2, UploadCloud, X,
} from 'lucide-react'
import { useApp } from '../App'
import { airtableAPI, menusAPI, printMaterialsAPI } from '../lib/api'
import { useToastActions } from '../components/Toast'
import ConfirmModal from '../components/ConfirmModal'
import ContainerLabels from '../components/ContainerLabels'
import AssetPreviewDialog from '../components/AssetPreviewDialog'
import {
  ACCEPTED_FILES, CARD_KINDS, KINDS, KIND_LABELS, MAX_FILE_BYTES,
  classifyFilename, defaultLabel, formatBytes, isPdf, matchStickersToDishes, normalizeName,
  printCards, printStickerLabels, printStickerSheet, printableStickerSheet, readImageSize, cardDemand, cardJobQuantity, demandCount, printQuantity, cardJobsDocument, printDocument,
} from '../lib/printMaterials'

const SINGLETON = new Set(CARD_KINDS.map((k) => k.kind))
const ALLOWED_EXT = new Set(['png', 'jpg', 'jpeg', 'webp', 'pdf'])

function formatWhen(value) {
  if (!value) return '—'
  const date = new Date(value.includes('T') ? value : `${value.replace(' ', 'T')}Z`)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function assetMeta(asset) {
  const dims = asset?.width && asset?.height ? `${asset.width} × ${asset.height}` : (isPdf(asset) ? 'PDF' : null)
  return [dims, formatBytes(asset?.size_bytes)].filter(Boolean).join(' · ')
}

// ── Small pieces ─────────────────────────────────────────────────────

function Stat({ icon: Icon, value, label, tone = 'green' }) {
  const color = tone === 'amber' ? 'text-amber-600' : 'text-havn-green-700'
  return (
    <div className="card p-4">
      {Icon ? <Icon className={`w-5 h-5 ${color}`} /> : <span className="block h-5" />}
      <strong className="font-display text-3xl text-havn-green-900 block mt-2 tabular-nums">{value}</strong>
      <span className="text-sm text-havn-gray-500">{label}</span>
    </div>
  )
}

function Thumb({ asset, className = '' }) {
  if (isPdf(asset)) {
    return (
      <span className={`flex flex-col items-center justify-center gap-1 text-havn-gray-500 ${className}`}>
        <FileImage className="w-8 h-8" />
        <span className="text-[11px] uppercase tracking-wider">PDF</span>
      </span>
    )
  }
  return <img src={asset.file_url} alt={asset.label} loading="lazy" className={`max-w-full max-h-full object-contain ${className}`} />
}

function CardSlot({ kindInfo, asset, onPreview, onPrint, onAdd, onRemove, printing, disabled }) {
  if (!asset) {
    return (
      <button
        type="button"
        onClick={() => onAdd(kindInfo.kind)}
        className="group min-h-[280px] rounded-xl border-2 border-dashed border-havn-cream-400 hover:border-havn-green-600 bg-havn-cream-50 hover:bg-white flex flex-col items-center justify-center gap-2 text-center p-4 transition-colors"
      >
        <span className="w-12 h-12 rounded-full bg-white border border-havn-cream-300 flex items-center justify-center text-havn-green-700 group-hover:bg-havn-green-700 group-hover:text-white transition-colors">
          <Plus className="w-5 h-5" />
        </span>
        <strong className="font-display text-xl text-havn-green-900">{kindInfo.label}</strong>
        <span className="text-xs text-havn-gray-500">Not saved yet · click to add</span>
      </button>
    )
  }
  return (
    <article className="rounded-xl border border-havn-cream-300 bg-white overflow-hidden flex flex-col">
      <button
        type="button"
        onClick={() => onPreview(asset)}
        className="relative bg-havn-cream-100 aspect-[3/4] flex items-center justify-center overflow-hidden group p-3"
        aria-label={`View ${kindInfo.label}`}
      >
        <Thumb asset={asset} className="shadow-soft" />
        <span className="absolute inset-0 bg-havn-green-900/0 group-hover:bg-havn-green-900/10 transition-colors" />
        <span className="absolute bottom-2 right-2 px-2 py-1 rounded-md bg-white/95 text-[11px] font-medium text-havn-green-900 inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <Maximize2 className="w-3 h-3" /> View
        </span>
      </button>
      <div className="p-3 border-t border-havn-cream-200">
        <strong className="block text-sm text-havn-charcoal">{kindInfo.label}</strong>
        <span className="block text-[11px] text-havn-gray-500 truncate">{assetMeta(asset)}</span>
        <p className="text-xs text-havn-green-800 mt-1">{asset.printQuantity === null ? 'Order count unavailable' : `${asset.baseCount} ${asset.kind === 'salad_card' ? 'salads' : 'orders'} → ${asset.printQuantity} copies`}</p>
        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          {!isPdf(asset) && (
            <button type="button" className="btn-primary !px-3 !py-1.5 !text-xs" onClick={() => onPrint(asset)} disabled={disabled || printing === asset.id || asset.printQuantity === null || asset.printQuantity === 0}>
              {printing === asset.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Printer className="w-3.5 h-3.5" />} Print {asset.printQuantity ?? "—"}
            </button>
          )}
          <a className="btn-secondary !px-3 !py-1.5 !text-xs" href={asset.download_url} download>
            <Download className="w-3.5 h-3.5" /> Download
          </a>
          <button type="button" className="btn-ghost !px-2 !py-1.5 !text-xs" onClick={() => onAdd(kindInfo.kind)} aria-label={`Replace ${kindInfo.label}`} title="Replace">
            <Replace className="w-3.5 h-3.5" />
          </button>
          <button type="button" className="btn-ghost !px-2 !py-1.5 !text-xs hover:!text-red-600" onClick={() => onRemove(asset)} aria-label={`Remove ${kindInfo.label}`} title="Remove">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </article>
  )
}

function StickerTile({ asset, onPreview, onPrint, onRemove, printing, disabled }) {
  return (
    <article className="rounded-xl border border-havn-cream-300 bg-white overflow-hidden">
      <button
        type="button"
        onClick={() => onPreview(asset)}
        className="relative w-full bg-havn-cream-100 aspect-[2/1] flex items-center justify-center overflow-hidden group p-2"
        aria-label={`View ${asset.label} sticker`}
      >
        <Thumb asset={asset} className="shadow-soft" />
        <span className="absolute inset-0 bg-havn-green-900/0 group-hover:bg-havn-green-900/10 transition-colors" />
      </button>
      <div className="px-3 py-2.5 border-t border-havn-cream-200 flex items-center gap-2">
        <div className="min-w-0 flex-1">
          <strong className="block text-sm text-havn-charcoal truncate">{asset.label}</strong>
          <span className="block text-[11px] text-havn-gray-500 truncate">
            {asset.dish ? asset.dish.category?.replace(/_/g, ' ') : 'not on this menu'}{assetMeta(asset) ? ` · ${assetMeta(asset)}` : ''}
          </span>
          {asset.kind === 'sticker' && <p className="text-xs text-havn-green-800 mt-1">{asset.printQuantity === null ? 'Match this sticker to a menu dish to get its quantity' : `${asset.baseCount} meals + ${asset.printQuantity - asset.baseCount} extra → ${asset.printQuantity} labels`}</p>}
        </div>
        {!isPdf(asset) && (
          <button type="button" className="btn-primary !px-2.5 !py-1.5 !text-xs" onClick={() => onPrint(asset)} disabled={disabled || printing === asset.id || asset.printQuantity === null || asset.printQuantity === 0} aria-label={`Print ${asset.label} sticker`} title="Print 2 × 1 in labels">
            {printing === asset.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Printer className="w-3.5 h-3.5" />}{asset.printQuantity != null ? ` ${asset.printQuantity}` : ''}
          </button>
        )}
        <a className="btn-ghost !px-2 !py-1.5 !text-xs" href={asset.download_url} download aria-label={`Download ${asset.label} sticker`} title="Download">
          <Download className="w-3.5 h-3.5" />
        </a>
        <button type="button" className="btn-ghost !px-2 !py-1.5 !text-xs hover:!text-red-600" onClick={() => onRemove(asset)} aria-label={`Remove ${asset.label} sticker`} title="Remove">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </article>
  )
}

// ── Upload review (Radix dialog: portaled, focus-trapped, Escape closes) ──

function UploadReviewDialog({ items, onClose, onChange, onRemoveItem, onSubmit, uploading, mirror, onMirror, sibling, menuName, cityName, existing }) {
  const open = items.length > 0
  const replacing = (item) => {
    if (SINGLETON.has(item.kind)) return existing.some((a) => a.kind === item.kind)
    return existing.some((a) => a.kind === item.kind && normalizeName(a.label) === normalizeName(item.label))
  }
  return (
    <Dialog.Root open={open} onOpenChange={(next) => { if (!next && !uploading) onClose() }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[150] bg-black/50 animate-fade-in" />
        <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[150] w-[min(96vw,780px)] max-h-[90vh] flex flex-col bg-white rounded-2xl shadow-2xl outline-none overflow-hidden">
          <header className="px-6 py-4 border-b border-havn-cream-300 flex items-start gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-[11px] uppercase tracking-[0.16em] text-havn-brass-600 font-medium">Save print materials</p>
              <Dialog.Title className="font-display text-2xl text-havn-green-900 leading-tight">{menuName} · {cityName}</Dialog.Title>
              <Dialog.Description className="text-sm text-havn-gray-500 mt-1">
                Check each file's type and name. A card replaces the one already saved; a sticker replaces the sticker with the same dish name.
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <button type="button" className="btn-ghost !px-2" aria-label="Close" disabled={uploading}><X className="w-5 h-5" /></button>
            </Dialog.Close>
          </header>

          <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-3">
            {items.map((item) => (
              <div key={item.key} className="flex items-center gap-3 p-3 rounded-xl border border-havn-cream-300 bg-havn-cream-50">
                <div className="w-20 h-14 rounded-lg bg-white border border-havn-cream-300 flex items-center justify-center overflow-hidden shrink-0">
                  {item.previewUrl ? <img src={item.previewUrl} alt="" className="max-w-full max-h-full object-contain" /> : <FileImage className="w-6 h-6 text-havn-gray-400" />}
                </div>
                <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <label className="block">
                    <span className="block text-[11px] uppercase tracking-wider text-havn-gray-500 mb-1">Type</span>
                    <select className="input !py-2" value={item.kind} onChange={(e) => onChange(item.key, { kind: e.target.value, label: defaultLabel(e.target.value, item.file.name) })}>
                      {KINDS.map((k) => <option key={k.kind} value={k.kind}>{k.label}</option>)}
                    </select>
                  </label>
                  <label className="block">
                    <span className="block text-[11px] uppercase tracking-wider text-havn-gray-500 mb-1">{item.kind === 'sticker' ? 'Dish name' : 'Name'}</span>
                    <input className="input !py-2" value={item.label} disabled={SINGLETON.has(item.kind)} onChange={(e) => onChange(item.key, { label: e.target.value })} />
                  </label>
                  <p className="sm:col-span-2 text-[11px] text-havn-gray-500 truncate">
                    {item.file.name} · {formatBytes(item.file.size)}{item.width ? ` · ${item.width} × ${item.height}` : ''}
                    {replacing(item) && <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-medium"><Replace className="w-3 h-3" /> replaces current</span>}
                  </p>
                </div>
                <button type="button" className="btn-ghost !px-2" onClick={() => onRemoveItem(item.key)} aria-label={`Remove ${item.file.name} from upload`} disabled={uploading}>
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>

          <footer className="px-6 py-4 border-t border-havn-cream-300 bg-havn-cream-50 flex flex-wrap items-center gap-3">
            {sibling ? (
              <label className="flex items-center gap-2 text-sm text-havn-charcoal mr-auto cursor-pointer">
                <input type="checkbox" className="w-4 h-4 accent-havn-green-700" checked={mirror} onChange={(e) => onMirror(e.target.checked)} disabled={uploading} />
                Also save to <strong>{sibling.city_name}</strong> <span className="text-havn-gray-500">({sibling.name})</span>
              </label>
            ) : (
              <span className="text-xs text-havn-gray-500 mr-auto">No matching menu in the other city for this week yet.</span>
            )}
            <button type="button" className="btn-secondary" onClick={onClose} disabled={uploading}>Cancel</button>
            <button type="button" className="btn-primary" onClick={onSubmit} disabled={uploading || items.some((i) => !i.label.trim())}>
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />}
              {uploading ? 'Saving…' : `Save ${items.length} file${items.length === 1 ? '' : 's'}`}
            </button>
          </footer>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

// ══════════════════════════════════════════════════════════════════════
// PAGE
// ══════════════════════════════════════════════════════════════════════

export default function PrintMaterials() {
  const { currentCity, currentMenu, menus, cities } = useApp()
  const { success, error: toastError } = useToastActions()

  const [data, setData] = useState(null)
  const [dishes, setDishes] = useState([])
  const [labelMenu, setLabelMenu] = useState(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(null)
  const [pending, setPending] = useState([])
  const [mirror, setMirror] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [preview, setPreview] = useState(null)
  const [removeTarget, setRemoveTarget] = useState(null)
  const [printing, setPrinting] = useState(null)
  const [extras, setExtras] = useState(5)
  const [orderCount, setOrderCount] = useState(null)
  const [orderError, setOrderError] = useState(null)
  const [frontKind, setFrontKind] = useState('menu_card')
  const [backKind, setBackKind] = useState('order_card')
  const [quantityBasis, setQuantityBasis] = useState('orders')
  const [manualCopies, setManualCopies] = useState('')
  const [paperMode, setPaperMode] = useState('letter')
  const [fitMode, setFitMode] = useState('fill')
  const [duplexMode, setDuplexMode] = useState('auto')
  const [flipMode, setFlipMode] = useState('long')
  const [cropMarks, setCropMarks] = useState(true)
  const loadVersion = useRef(0)
  const [dragDepth, setDragDepth] = useState(0)
  const fileInputRef = useRef(null)
  const presetKindRef = useRef(null)

  const menuFromContext = menus.find((m) => m.id === currentMenu)
  const menuName = data?.menu?.name || menuFromContext?.name || 'No menu selected'
  const cityName = data?.menu?.city_name || cities.find((c) => c.id === currentCity)?.name || currentCity?.toUpperCase() || ''

  const load = useCallback(async () => {
    const version = ++loadVersion.current
    setData(null)
    setDishes([])
    setLabelMenu(null)
    setOrderCount(null)
    setManualCopies('')
    setPreview(null)
    setLoadError(null)
    setOrderError(null)
    if (!currentMenu) { setLoading(false); return }
    setLoading(true)
    try {
      const [pack, menu, imported] = await Promise.all([
        printMaterialsAPI.get(currentMenu), menusAPI.get(currentMenu),
        airtableAPI.getLatest(currentCity, currentMenu).catch(() => ({ countError: true })),
      ])
      if (version !== loadVersion.current) return
      setData(pack)
      setDishes(menu?.dishes || [])
      setLabelMenu(menu)
      const importedOrderCount = demandCount(imported?.order_count)
      setOrderCount(importedOrderCount)
      if (importedOrderCount === null) {
        setQuantityBasis((current) => current === 'orders' ? 'manual' : current)
        setOrderError(imported?.countError ? 'Could not load order counts. Enter the exact number of copies below or retry.' : 'No order count has been imported for this menu. Enter the exact number of copies below.')
      }
    } catch (err) {
      if (version === loadVersion.current) setLoadError(err.message || 'Could not load print materials')
    } finally {
      if (version === loadVersion.current) setLoading(false)
    }
  }, [currentMenu, currentCity])

  useEffect(() => { load(); return () => { loadVersion.current += 1 } }, [load])
  useEffect(() => { setQuantityBasis('orders') }, [currentMenu, currentCity])

  // Clean up staged preview URLs when the review closes.
  useEffect(() => () => { pending.forEach((p) => p.previewUrl && URL.revokeObjectURL(p.previewUrl)) }, [pending])

  const assets = data?.assets || []
  const sibling = data?.sibling_menus?.[0] || null
  const cards = CARD_KINDS.map((k) => {
    const asset = assets.find((a) => a.kind === k.kind)
    const baseCount = cardDemand(k.kind, dishes, orderCount)
    return { ...k, asset: asset ? { ...asset, baseCount, printQuantity: printQuantity(baseCount, extras) } : null }
  })
  const cardAssets = cards.map((c) => c.asset).filter(Boolean)
  const stickers = useMemo(() => assets.filter((a) => a.kind === 'sticker'), [assets])
  const others = useMemo(() => assets.filter((a) => a.kind === 'other'), [assets])
  const { ordered: matchedStickers, missingDishes } = useMemo(() => matchStickersToDishes(stickers, dishes), [stickers, dishes])
  const orderedStickers = matchedStickers.map((asset) => {
    const baseCount = demandCount(asset.dish?.meal_count)
    return { ...asset, baseCount, printQuantity: printQuantity(baseCount, extras) }
  })
  const previewList = useMemo(() => [...cardAssets, ...orderedStickers, ...others], [cardAssets, orderedStickers, others])
  const ready = !loading && !loadError && String(labelMenu?.id) === String(currentMenu) && data?.menu?.city_id === currentCity
  const { stickers: sheetStickers, unavailable: unavailableSheetStickers } = printableStickerSheet(orderedStickers)
  const missingStickerCounts = unavailableSheetStickers.length > 0
  const stickerTotal = sheetStickers.reduce((sum, s) => sum + s.printQuantity, 0)
  const sheetPages = Math.ceil(stickerTotal / 40)
  const printableCards = cardAssets.filter((a) => !isPdf(a))
  const canPrintCards = ready && printableCards.length > 0 && printableCards.every((a) => a.printQuantity !== null) && printableCards.some((a) => a.printQuantity > 0)
  const front = cardAssets.find((a) => a.kind === frontKind)
  const back = cardAssets.find((a) => a.kind === backKind)
  const { base: jobBase, copies: jobQuantity, exact: jobQuantityIsExact } = cardJobQuantity(quantityBasis, dishes, orderCount, manualCopies, extras)
  const canPrintJob = ready && front && !isPdf(front) && (!backKind || (back && !isPdf(back))) && jobQuantity > 0
  const cardPrintOptions = { paper: paperMode, fitMode, flipMode, cropMarks, duplexMode }


  // ── Staging + upload ──
  const openPicker = (presetKind = null) => {
    presetKindRef.current = presetKind
    if (fileInputRef.current) {
      fileInputRef.current.multiple = !presetKind || presetKind === 'sticker' || presetKind === 'other'
      fileInputRef.current.click()
    }
  }

  const stageFiles = async (fileList, presetKind = null) => {
    const files = Array.from(fileList || [])
    if (!files.length) return
    const items = []
    const rejected = []
    for (const file of files) {
      const ext = file.name.split('.').pop().toLowerCase()
      if (!ALLOWED_EXT.has(ext)) { rejected.push(file.name); continue }
      if (file.size > MAX_FILE_BYTES) { rejected.push(`${file.name} (over 25 MB)`); continue }
      const size = await readImageSize(file)
      const kind = presetKind || classifyFilename(file.name, size?.width, size?.height)
      items.push({
        key: `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2, 7)}`,
        file, kind, label: defaultLabel(kind, file.name),
        width: size?.width, height: size?.height,
        previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
      })
    }
    if (rejected.length) toastError(`Skipped ${rejected.join(', ')} — only PNG, JPG, WebP, or PDF up to 25 MB can be saved`)
    if (items.length) {
      setMirror(true)
      setPending((prev) => [...prev, ...items])
    }
  }

  const updatePending = (key, patch) => setPending((prev) => prev.map((p) => (p.key === key ? { ...p, ...patch } : p)))
  const removePending = (key) => setPending((prev) => prev.filter((p) => p.key !== key))
  const clearPending = () => setPending([])

  const submitUpload = async () => {
    if (!pending.length || !currentMenu) return
    const form = new FormData()
    pending.forEach((p) => form.append('files', p.file, p.file.name))
    form.append('kinds', JSON.stringify(pending.map((p) => p.kind)))
    form.append('labels', JSON.stringify(pending.map((p) => p.label.trim())))
    form.append('mirror', mirror && sibling ? 'true' : 'false')
    form.append('replace', 'true')
    setUploading(true)
    try {
      const result = await printMaterialsAPI.upload(currentMenu, form)
      const count = pending.length
      const mirrored = result.mirrored_to?.length ? ` and ${sibling.city_name}` : ''
      success(`Saved ${count} file${count === 1 ? '' : 's'} to ${menuName} for ${cityName}${mirrored}`)
      clearPending()
      await load()
    } catch (err) {
      toastError(err.message || 'Upload failed')
    } finally {
      setUploading(false)
    }
  }

  // ── Printing ──
  const runPrint = async (key, fn) => {
    setPrinting(key)
    try { await fn() } catch (err) { toastError(err.message || 'Could not print') } finally { setPrinting(null) }
  }
  const printOne = (asset) => {
    if (!ready || printing) return
    const current = previewList.find((a) => a.id === asset.id)
    if (!current || current.printQuantity === null || current.printQuantity === 0) { toastError('No print quantity available. Check the counts for this menu.'); return }
    return runPrint(current.id, () => (current.kind === 'sticker' ? printStickerLabels(current, current.printQuantity) : printCards([current], current.label)))
  }

  // ── Remove ──
  const confirmRemove = async () => {
    if (!removeTarget) return
    try {
      await printMaterialsAPI.remove(removeTarget.id)
      success(`Removed ${removeTarget.label} from ${cityName}`)
      if (preview?.id === removeTarget.id) setPreview(null)
      await load()
    } catch (err) {
      toastError(err.message || 'Could not remove')
    }
  }

  // ── Drag & drop of files onto the page ──
  const onDragEnter = (e) => { if (e.dataTransfer?.types?.includes('Files')) { e.preventDefault(); setDragDepth((d) => d + 1) } }
  const onDragLeave = (e) => { if (e.dataTransfer?.types?.includes('Files')) { e.preventDefault(); setDragDepth((d) => Math.max(0, d - 1)) } }
  const onDragOver = (e) => { if (e.dataTransfer?.types?.includes('Files')) e.preventDefault() }
  const onDrop = (e) => { if (e.dataTransfer?.files?.length) { e.preventDefault(); setDragDepth(0); stageFiles(e.dataTransfer.files) } }

  if (!currentMenu) {
    return (
      <div className="card p-10 text-center">
        <Printer className="w-8 h-8 mx-auto text-havn-green-700" />
        <h1 className="font-display text-2xl text-havn-green-900 mt-3">Print materials</h1>
        <p className="text-sm text-havn-gray-500 mt-1">Pick a menu in the sidebar to see its print pack.</p>
      </div>
    )
  }

  return (
    <div className="space-y-6 relative" onDragEnter={onDragEnter} onDragLeave={onDragLeave} onDragOver={onDragOver} onDrop={onDrop}>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={ACCEPTED_FILES}
        className="hidden"
        aria-label="Choose print materials to save"
        onChange={(e) => { const preset = presetKindRef.current; presetKindRef.current = null; stageFiles(e.target.files, preset); e.target.value = '' }}
      />

      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.16em] text-havn-brass-600 font-medium">Print center</p>
          <h1 className="font-display text-3xl text-havn-green-900 mt-1">Print materials</h1>
          <p className="text-sm text-havn-gray-500 mt-1">
            {menuName} · {cityName} · saved with this menu, so every past week's pack stays here
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" className="btn-secondary" onClick={() => runPrint('cards', () => printCards(cardAssets, `${menuName} cards`))} disabled={!canPrintCards || !!printing}>
            {printing === 'cards' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />} Print all cards · single-sided
          </button>
          {assets.length > 0 ? (
            <a className="btn-secondary" href={printMaterialsAPI.zipUrl(currentMenu)} download>
              <FileDown className="w-4 h-4" /> Download all
            </a>
          ) : (
            <button type="button" className="btn-secondary" disabled><FileDown className="w-4 h-4" /> Download all</button>
          )}
          <button type="button" className="btn-primary" onClick={() => openPicker()}>
            <UploadCloud className="w-4 h-4" /> Add files
          </button>
        </div>
      </div>

      {loadError && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" /> {loadError}
          <button type="button" className="btn-ghost !py-1 !px-2 !text-xs ml-auto" onClick={load}>Retry</button>
        </div>
      )}

      {loading && !data ? (
        <div className="card p-10 flex items-center justify-center gap-3 text-havn-gray-500">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading print materials…
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Stat icon={FileImage} value={`${cardAssets.length}/${CARD_KINDS.length}`} label="cards saved" />
            <Stat icon={StickerIcon} value={stickers.length} label="stickers saved" />
            <Stat
              icon={missingDishes.length ? AlertTriangle : CheckCircle2}
              tone={missingDishes.length ? 'amber' : 'green'}
              value={missingDishes.length}
              label={`dish${missingDishes.length === 1 ? '' : 'es'} without a sticker`}
            />
            <Stat value={formatWhen(data?.last_updated)} label="last updated" />
          </div>

          {!assets.length && (
            <div className="card p-10 text-center border-2 border-dashed !border-havn-cream-400">
              <UploadCloud className="w-8 h-8 mx-auto text-havn-green-700" />
              <h2 className="font-display text-2xl text-havn-green-900 mt-3">Nothing saved for {menuName} yet</h2>
              <p className="text-sm text-havn-gray-500 mt-1 max-w-xl mx-auto">
                Drop the week's pack anywhere on this page, or click Add files. Files are sorted into cards and stickers by name, and you can fix anything before saving.
              </p>
              <button type="button" className="btn-primary mt-5" onClick={() => openPicker()}><UploadCloud className="w-4 h-4" /> Add files</button>
            </div>
          )}

          <section className="card p-4 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="font-display text-lg text-havn-green-900">Automatic print quantities</h2>
                <p className="text-xs text-havn-gray-500">Each dish uses its saved meal count. General cards use orders; salad cards use salads. Zero orders means zero copies.</p>
              </div>
              <label className="flex items-center gap-2 text-sm">Extra copies
                <select className="input !w-24" value={extras} onChange={(e) => setExtras(Number(e.target.value))} disabled={!!printing}>
                  <option value={5}>+5</option><option value={10}>+10</option>
                </select>
              </label>
            </div>
            <div className="flex flex-wrap gap-4 text-sm text-havn-green-900">
              <span>{orderCount ?? '—'} orders</span>
              <span>{stickerTotal} printable labels · {sheetPages} letter sheets</span>
              <button className="btn-ghost !py-0 !text-xs" onClick={load} disabled={loading || !!printing}>Refresh counts</button>
            </div>
            {orderError && <p role="status" className="text-sm text-amber-800">{orderError}</p>}
          </section>

          <section className="card">
            <div className="card-header">
              <h2 className="font-display text-lg text-havn-green-900">5 × 7 front & back cards</h2>
              <p className="text-xs text-havn-gray-500">Print exact 5 × 7 cards on letter paper with crop marks or directly on 5 × 7 cardstock. The quantity includes your extra copies.</p>
            </div>
            <div className="p-4 space-y-4">
              <div className="grid sm:grid-cols-3 gap-4">
                <label className="text-sm">Front
                  <select className="input mt-1" value={frontKind} onChange={(e) => {
                    const nextKind = e.target.value
                    setFrontKind(nextKind)
                    setQuantityBasis((current) => current === 'manual' ? 'manual' : nextKind === 'salad_card' ? 'salads' : orderCount === null ? 'manual' : 'orders')
                  }}>
                    {cards.map((c) => <option key={c.kind} value={c.kind} disabled={!c.asset || isPdf(c.asset)}>{c.label}{!c.asset ? ' — not saved' : isPdf(c.asset) ? ' — PDF, download separately' : ''}</option>)}
                  </select>
                </label>
                <label className="text-sm">Back
                  <select className="input mt-1" value={backKind} onChange={(e) => setBackKind(e.target.value)}>
                    <option value="">Blank · single-sided</option>
                    {cards.map((c) => <option key={c.kind} value={c.kind} disabled={!c.asset || isPdf(c.asset)}>{c.label}{!c.asset ? ' — not saved' : isPdf(c.asset) ? ' — PDF, download separately' : ''}</option>)}
                  </select>
                </label>
                <label className="text-sm">Quantity based on
                  <select className="input mt-1" value={quantityBasis} onChange={(e) => setQuantityBasis(e.target.value)}>
                    <option value="orders" disabled={orderCount === null}>All orders{orderCount === null ? ' — not imported' : ''}</option><option value="salads">All salad meals</option>
                    {dishes.map((d) => <option key={d.dish_id} value={String(d.dish_id)}>{d.name}</option>)}
                    <option value="manual">Manual copies</option>
                  </select>
                </label>
              </div>
              {quantityBasis === 'manual' && <label className="block max-w-xs text-sm font-medium text-havn-charcoal">Exact copies to print
                <input type="number" min="1" step="1" inputMode="numeric" className="input mt-1" value={manualCopies} onChange={(e) => setManualCopies(e.target.value)} placeholder="Enter copy count" aria-describedby="manual-card-copy-help" />
                <span id="manual-card-copy-help" className="block mt-1 text-xs font-normal text-havn-gray-500">This is the final number of cards. Extra copies are not added again.</span>
              </label>}
              <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 rounded-xl border border-havn-cream-300 bg-havn-cream-50 p-4">
                <label className="text-sm">Paper
                  <select className="input mt-1" value={paperMode} onChange={(e) => setPaperMode(e.target.value)} disabled={!!printing}>
                    <option value="letter">Letter · cut to 5 × 7</option><option value="card">5 × 7 cardstock</option>
                  </select>
                </label>
                <label className="text-sm">Artwork fit
                  <select className="input mt-1" value={fitMode} onChange={(e) => setFitMode(e.target.value)} disabled={!!printing}>
                    <option value="fill">Fill card · trim edges</option><option value="contain">Fit whole artwork</option>
                  </select>
                </label>
                <label className="text-sm">Printing
                  <select className="input mt-1" value={duplexMode} onChange={(e) => setDuplexMode(e.target.value)} disabled={!!printing || !backKind}>
                    <option value="auto">Automatic duplex</option><option value="manual">Manual front / back</option>
                  </select>
                </label>
                <label className="text-sm">Flip edge
                  <select className="input mt-1" value={flipMode} onChange={(e) => setFlipMode(e.target.value)} disabled={!!printing || !backKind}>
                    <option value="long">Long edge</option><option value="short">Short edge</option>
                  </select>
                </label>
                <label className="text-sm flex items-center gap-2 self-end min-h-[42px]">
                  <input type="checkbox" className="w-4 h-4 accent-havn-green-700" checked={cropMarks} onChange={(e) => setCropMarks(e.target.checked)} disabled={!!printing || paperMode !== 'letter'} /> Crop marks
                </label>
              </div>
              <div className="flex flex-wrap items-center gap-4">
                {[['Front', front], ['Back', backKind ? back : null]].map(([label, asset]) => <div key={label} className="text-center">
                  <div className="w-20 h-24 border border-havn-cream-300 bg-havn-cream-50 p-1 flex items-center justify-center">{asset ? <Thumb asset={asset} /> : <span className="text-xs text-havn-gray-500">{label === 'Back' && !backKind ? 'Blank' : 'Not saved'}</span>}</div>
                  <span className="text-xs text-havn-gray-500">{label}</span>
                </div>)}
                <div className="flex-1 min-w-[180px]">
                  <strong className="block text-lg text-havn-green-900">{jobQuantity === null ? (quantityBasis === 'manual' ? 'Enter the number of copies' : 'Quantity unavailable') : jobQuantityIsExact ? `${jobQuantity} exact copies` : `${jobBase} + ${jobQuantity - jobBase} extra = ${jobQuantity} sheets`}</strong>
                  <p className="text-xs text-havn-gray-500">Print at Actual size / 100%. {backKind && duplexMode === 'auto' ? `Choose two-sided printing, flip on ${flipMode} edge.` : backKind ? `Print fronts first, reload the same sheets by flipping on the ${flipMode} edge, then print backs.` : 'Choose single-sided printing.'} Leave Copies at 1 — all copies are already included.</p>
                </div>
                {backKind && duplexMode === 'manual' ? <div className="flex flex-wrap gap-2">
                  <button className="btn-primary" disabled={!canPrintJob || !!printing} onClick={() => runPrint('fronts', () => printDocument(cardJobsDocument([{ front, back, copies: jobQuantity }], `${menuName} — ${jobQuantity} fronts`, { ...cardPrintOptions, side: 'front' })))}><Printer className="w-4 h-4" /> 1. Print {jobQuantity ?? '—'} fronts</button>
                  <button className="btn-secondary" disabled={!canPrintJob || !!printing} onClick={() => runPrint('backs', () => printDocument(cardJobsDocument([{ front, back, copies: jobQuantity }], `${menuName} — ${jobQuantity} backs`, { ...cardPrintOptions, side: 'back' })))}><Printer className="w-4 h-4" /> 2. Print {jobQuantity ?? '—'} backs</button>
                </div> : <button className="btn-primary" disabled={!canPrintJob || !!printing} onClick={() => runPrint('duplex', () => printDocument(cardJobsDocument([{ front, back: backKind ? back : null, copies: jobQuantity }], `${menuName} — ${jobQuantity} cards`, { ...cardPrintOptions, side: backKind ? 'duplex' : 'front' })))}>
                  <Printer className="w-4 h-4" /> Print {jobQuantity ?? '—'} cards
                </button>}
              </div>
            </div>
          </section>

          <ContainerLabels key={currentMenu} menu={labelMenu} disabled={loading || !!loadError || !!printing || String(labelMenu?.id) !== String(currentMenu)} runPrint={runPrint} />

          <section className="card">
            <div className="card-header">
              <h2 className="font-display text-lg text-havn-green-900">Cards</h2>
              <p className="text-xs text-havn-gray-500">Exact 5 × 7 cards centered on letter paper with crop marks. Quantities include extras. Print at Actual size, choose single-sided, and leave Copies at 1.</p>
            </div>
            <div className="p-4 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
              {cards.map((card) => (
                <CardSlot
                  key={card.kind}
                  kindInfo={card}
                  asset={card.asset}
                  onPreview={setPreview}
                  onPrint={printOne}
                  onAdd={openPicker}
                  onRemove={setRemoveTarget}
                  printing={printing}
                  disabled={!ready || !!printing}
                />
              ))}
            </div>
          </section>

          <section className="card">
            <div className="card-header flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="font-display text-lg text-havn-green-900">Stickers</h2>
                <p className="text-xs text-havn-gray-500">2 × 1 in container labels · {stickers.length} saved · listed in menu order. Leave printer Copies at 1.</p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => runPrint('sheet', () => printStickerSheet(sheetStickers))}
                  disabled={!ready || !sheetPages || !!printing}
                  title={sheetPages ? 'Letter sheets, 4 across × 10 down, no gutters' : 'No matched stickers with meal quantities are ready to print'}
                >
                  {printing === 'sheet' ? <Loader2 className="w-4 h-4 animate-spin" /> : <LayoutGrid className="w-4 h-4" />}
                  Print sheet{sheetPages ? ` · ${sheetPages} page${sheetPages === 1 ? '' : 's'}` : ''}
                </button>
                <button type="button" className="btn-ghost" onClick={() => openPicker('sticker')}>
                  <Plus className="w-4 h-4" /> Add stickers
                </button>
              </div>
            </div>

            {missingStickerCounts && <p role="status" className="px-4 pt-4 text-sm text-amber-800">{unavailableSheetStickers.map((sticker) => sticker.label).join(', ')} {unavailableSheetStickers.length === 1 ? 'has' : 'have'} no matched meal quantity and will be left off the sheet. The matched stickers can still print.</p>}
            {orderedStickers.some(isPdf) && <p className="px-4 pt-4 text-sm text-amber-800">PDF stickers are downloaded separately and are not included in the letter sheets.</p>}
            {missingDishes.length > 0 && (
              <div className="mx-4 mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 flex gap-3">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <div>
                  <strong className="font-medium">No sticker saved yet for:</strong>{' '}
                  {missingDishes.map((d) => d.name).join(', ')}
                </div>
              </div>
            )}

            {orderedStickers.length ? (
              <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4">
                {orderedStickers.map((sticker) => (
                  <StickerTile key={sticker.id} asset={sticker} onPreview={setPreview} onPrint={printOne} onRemove={setRemoveTarget} printing={printing} disabled={!ready || !!printing} />
                ))}
              </div>
            ) : (
              <div className="p-8 text-center text-sm text-havn-gray-500">No stickers saved for this menu yet.</div>
            )}
          </section>

          {others.length > 0 && (
            <section className="card">
              <div className="card-header">
                <h2 className="font-display text-lg text-havn-green-900">Other materials</h2>
                <p className="text-xs text-havn-gray-500">Anything else saved with this week's pack.</p>
              </div>
              <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4">
                {others.map((asset) => (
                  <StickerTile key={asset.id} asset={asset} onPreview={setPreview} onPrint={printOne} onRemove={setRemoveTarget} printing={printing} disabled={!ready || !!printing} />
                ))}
              </div>
            </section>
          )}
        </>
      )}

      {dragDepth > 0 && (
        <div className="fixed inset-0 z-[140] bg-havn-green-900/70 backdrop-blur-sm flex items-center justify-center pointer-events-none">
          <div className="bg-white rounded-2xl px-10 py-8 text-center shadow-2xl">
            <UploadCloud className="w-10 h-10 mx-auto text-havn-green-700" />
            <p className="font-display text-2xl text-havn-green-900 mt-2">Drop to add to {menuName}</p>
            <p className="text-sm text-havn-gray-500">You'll review names and types before anything is saved.</p>
          </div>
        </div>
      )}

      <UploadReviewDialog
        items={pending}
        onClose={clearPending}
        onChange={updatePending}
        onRemoveItem={removePending}
        onSubmit={submitUpload}
        uploading={uploading}
        mirror={mirror}
        onMirror={setMirror}
        sibling={sibling}
        menuName={menuName}
        cityName={cityName}
        existing={assets}
      />

      <AssetPreviewDialog
        asset={preview}
        assets={previewList}
        onClose={() => setPreview(null)}
        onNavigate={setPreview}
        onPrint={printOne}
        printing={preview && printing === preview.id}
      />

      <ConfirmModal
        isOpen={Boolean(removeTarget)}
        onClose={() => setRemoveTarget(null)}
        onConfirm={confirmRemove}
        title={`Remove ${removeTarget?.label || 'this file'}?`}
        message={`It will be removed from ${menuName} for ${cityName} only${sibling ? `. ${sibling.city_name}'s copy is kept` : ''}.`}
        confirmText="Remove"
        variant="danger"
      />
    </div>
  )
}
